import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useClientTags } from "../hooks/useClientTags";
import { formatTagMatchLabel, normalizeTagMatchValue } from "../utils/automationTagMatch";

export default function AutomationTagMatchField({
  name,
  label,
  value,
  helperText,
  errorText,
  required,
  source = "lead",
  onChange,
  onLabel,
}) {
  const parsed = useMemo(() => normalizeTagMatchValue(value), [value]);
  const { data: catalog = [], isLoading } = useClientTags({
    search: "",
    limit: 200,
    includeUsed: true,
  });

  const options = useMemo(() => {
    const fromCatalog = (catalog || [])
      .filter((t) => (t?.status == null ? true : Number(t.status) === 1))
      .map((t) => String(t?.nome || t?.name || "").trim())
      .filter(Boolean);
    return [...new Set([...fromCatalog, ...parsed.tags])];
  }, [catalog, parsed.tags]);

  const emit = (tags, mode) => {
    const next = { tags, mode };
    onChange?.(name, next);
    onLabel?.(name, formatTagMatchLabel(next));
  };

  return (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        options={options}
        value={parsed.tags}
        loading={isLoading}
        filterSelectedOptions
        onChange={(_e, next) => {
          const tags = (next || [])
            .map((item) => String(item || "").trim())
            .filter(Boolean);
          emit(tags, parsed.mode);
        }}
        renderTags={(selected, getTagProps) =>
          selected.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option} size="small" label={option} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            margin="dense"
            label={label}
            required={!!required}
            error={!!errorText}
            helperText={
              errorText ||
              helperText ||
              "Digite ou escolha uma ou mais tags do catálogo."
            }
            placeholder={parsed.tags.length ? "" : "Buscar tag…"}
          />
        )}
      />

      {parsed.tags.length > 1 ? (
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            {source === "client" ? "O cadastro precisa ter" : "A solicitação precisa ter"}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={parsed.mode}
            onChange={(_e, next) => {
              if (!next) return;
              emit(parsed.tags, next);
            }}
            aria-label="Modo de combinação das tags"
          >
            <ToggleButton value="any" sx={{ textTransform: "none", px: 1.5 }}>
              Pelo menos uma
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5 }}>
              Todas
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      ) : null}
    </Box>
  );
}
